import { Button, Card, Tag } from 'antd';
import React from 'react';
import { useQuery } from 'react-query';
import { isNil } from 'lodash';

import { toCurrency } from 'utils/utils';
import { getGuarantyListByCTRID } from 'services/guaranty';
import DataGrid, { DataGridColDef } from 'components/DataGrid/DataGrid';
import TasksListPopover from 'pages/workflow/TasksListPopover';
import IActions from 'icons/IActions';
import GetLabel from 'DisplayFramework/components/GetLabel';
import moment from 'moment';
import { CheckCircleOutlined, SyncOutlined } from '@ant-design/icons';
import ThirdPartyModal from 'pages/ThirdParty/ThirdPartyDetail/ThirdPartyModal';
import GuarantDetailModal from 'pages/guaranty/guarantyDetails/guarantyDetailsModal';
import ModalControllerNV from 'components/ModalControllerNV';

type GuaranteesTableProps = {
  ctrid?: number;
  onOpenGuarantyDetail?: (id: number) => void;
  display?: boolean;
  currcode?: string;
  refetchProp?: any;
};

const GuaranteesTable = (props: GuaranteesTableProps) => {
  const { ctrid, display, currcode, refetchProp } = props;

  const {
    data: guarantees,
    isFetching,
    refetch,
  } = useQuery(
    ['getGuarantyListByCTRID', ctrid, refetchProp],
    () =>
      getGuarantyListByCTRID({ ctrid: ctrid! }).then((res) => res.list || []),
    { enabled: !isNil(ctrid) }
  );

  const colorTag = (status: string) => {
    if (status === 'ACTIVE') {
      return 'success';
    } else if (status === 'INIT') {
      return 'default';
    } else if (status === 'INPROG') {
      return 'warning';
    } else if (status === 'CANCEL') {
      return 'error';
    } else if (status === 'TERMINATED') {
      return 'geekblue';
    }
    return '';
  };

  const columns: DataGridColDef<any>[] = [
    {
      dataIndex: 'guarreference',
      width: 150,
      align: 'left',
      render: (value, record) => {
        return (
          <ModalControllerNV
            content={({ setVisible, visible }) => (
              <a
                onClick={() => {
                  setVisible(true);
                }}
              >
                {record.guarreference}
              </a>
            )}
            modal={({ setVisible, visible }) => (
              <GuarantDetailModal
                guarId={record.guarid}
                visible={visible}
                onCancel={() => setVisible(false)}
                onSuccess={() => setVisible(false)}
                title={record.guarantorreference + '-' + record.guarantorname}
                iscreate={false}
              />
            )}
          />
        );
      },
    },

    {
      comcode: 'guaranty.deccodelabel',
      dataIndex: 'guartypelabel',
      width: 200,
      fixed: 'left',
    },
    {
      comcode: 'guaranty.guarantorname',
      dataIndex: 'guarantorname',
      width: 250,
      align: 'left',
      render: (value, record) => {
        return (
          <ModalControllerNV
            content={({ setVisible, visible }) => (
              <a
                onClick={() => {
                  setVisible(true);
                }}
              >
                {record.guarantorname}
              </a>
            )}
            modal={({ setVisible, visible }) => (
              <ThirdPartyModal
                tpId={record.tpidguarantor}
                visible={visible}
                onCancel={() => setVisible(false)}
                onSuccess={() => setVisible(false)}
                title={record.guarantorname}
                iscreate={false}
              />
            )}
          />
        );
      },
    },
    {
      comcode: 'guaranty.guaramounts',
      dataIndex: 'guaramount',
      width: 150,
      align: 'left',
      render: (value, record) => {
        return toCurrency(record.guaramount, record.currcode ?? currcode);
      },
    },
    {
      comcode: 'guaranty.conguarantycoverageamount',
      dataIndex: 'conguarantycoverageamount',
      width: 150,
      align: 'left',
      fixed: 'right',
      render: (value, record) => {
        return toCurrency(
          record.conguarantycoverageamount,
          record.currcode ?? currcode
        );
      },
    },
    {
      comcode: 'guaranty.conguarantystatuslabel',
      dataIndex: 'conguarantystatuslabel',
      width: 100,
      align: 'left',
      render: (value, record) => {
        return (
          <Tag
            icon={
              (record.guarstatus === 'ACTIVE' && <CheckCircleOutlined />) ||
              (record.guarstatus === 'INPROG' && <SyncOutlined spin />)
            }
            color={colorTag(record.guarstatus)}
          >
            {record.guarstatuslabel}
          </Tag>
        );
      },
    },
    {
      comcode: 'guaranty.conguarantystatusdate',
      dataIndex: 'conguarantystatusdate',
      width: 100,
      align: 'left',
      render: (eventdate) => moment(eventdate).format('YYYY-MM-DD'),
    },
    {
      comcode: 'Action',
      dataIndex: 'operation',
      render: (_, record) => (
        <TasksListPopover
          entityId={record.guarid}
          entityReference={record?.guarreference}
          entityName="GUARANTY"
          title={record?.guarreference + ' - ' + record?.beneficiaryname}
          onClose={refetch}
        >
          <Button size="small" icon={<IActions height="15px" width="15px" />}>
            {' '}
            <GetLabel label="actions" />
          </Button>
        </TasksListPopover>
      ),
      fixed: 'right',
      width: 80,
    },
  ];

  return (
    <Card
      title={
        <GetLabel
          label="guarante"
          style={{ fontWeight: 'bold', paddingLeft: 15 }}
        />
      }
      style={{
        width: '100%',
        boxShadow:
          'rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px',
      }}
      bordered
      className="p-13"
    >
      <DataGrid
        columns={
          display
            ? columns.filter((col) => col.dataIndex !== 'operation')
            : columns
        }
        pagination={{ responsive: true, hideOnSinglePage: true }}
        dataSource={guarantees}
        isLoading={isFetching}
        key="guarid"
        titleComcode="guarantyList"
      />
    </Card>
  );
};

export default GuaranteesTable;
